import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOnlineOrderWaitingPaymentCancel } from './logic/use-online-order-waiting-payment-cancel';
import { useOnlineOrderDetailPageQuery } from '@app/page/online-order/detail/logic/use-online-order-detail-page-query';
import { getFragmentData } from '@app/graphql/types';
import { OnlineOrderFragment } from '@app/page/online-order/model/online-order-fragment';
import { OnlineOrderUserState } from '@app/graphql/types/graphql';
import { captureException } from '@sentry/react';
import { useTranslate } from '@tolgee/react';
import SuperButton from '@app/components/super-button';
import Spinner from '@app/components/spinner';
import ApplicationErrorView from '@app/module/error/application-error-view';
import Loading from '@app/components/loading';
import { ApolloError } from '@apollo/client';

export const OnlineOrderCancelPage: React.FC = () => {
    const { onlineOrderID } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslate();
    const [cancelPayment, { error: cancelError, loading: cancelLoading }] = useOnlineOrderWaitingPaymentCancel();
    const { data, loading, error } = useOnlineOrderDetailPageQuery(onlineOrderID);
    const onlineOrder = getFragmentData(OnlineOrderFragment, data?.storefront_onlineOrder);

    useEffect(() => {
        if (loading) {
            return;
        }

        if (onlineOrderID) {
            if (onlineOrder?.userState === OnlineOrderUserState.WaitingForPayment) {
                cancelPayment({
                    variables: {
                        onlineOrderID,
                    },
                })
                    .then(() => {
                        navigate(`/online-order/${onlineOrderID}`);
                    })
                    .catch(captureException);
            } else {
                navigate(`/online-order/${onlineOrderID}`);
            }
        }
    }, [onlineOrderID, onlineOrder?.userState, cancelPayment, navigate, loading]);

    const handleTryAgain = () => {
        if (onlineOrderID) {
            navigate(`/online-order/${onlineOrderID}`);
        }
    };

    if (loading) {
        return <Loading />;
    }

    if (error) {
        return <ApplicationErrorView error={error} />;
    }

    if (!onlineOrder) {
        return <ApplicationErrorView error={new ApolloError({})} />;
    }

    return (
        <div className="payment-page payment-page--cancel flex flex-col items-center justify-center min-h-screen p-4">
            <div className="payment-page__container max-w-md w-full bg-white rounded-lg shadow-md p-6">
                <ApplicationErrorView error={cancelError} />
                <h1 className="payment-page__title text-2xl font-bold text-center mb-4">{t('online-order.payment.cancel.title', 'Payment Cancelled')}</h1>
                <p className="payment-page__message text-center mb-6 text-gray-600">
                    {t('online-order.payment.cancel.description', 'Your payment was cancelled. You can try again or contact support if you need assistance.')}
                </p>
                {onlineOrderID && (
                    <SuperButton
                        className="payment-page__try-again-btn w-full"
                        onClick={handleTryAgain}
                        disabled={cancelLoading}
                    >
                        {cancelLoading ? <Spinner /> : t('online-order.payment.cancel.try-again', 'Try Again')}
                    </SuperButton>
                )}
            </div>
        </div>
    );
}; 